// Style imports
/* eslint-disable */
import 'ag-grid-root/dist/styles/ag-grid.css';
/* eslint-enable */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import gridOptions, { helpText, moreInfoHTML } from 'Config/tableConfig';
import TableHeader from 'Components/tableHeader';
import TableCellFullWidth from 'Components/tableCellFullWidth';
import TableCell from 'Components/tableCell';
import TableCellCharacteristic from 'Components/tableCellCharacteristic';
import TableToolbar from 'Components/tableToolbar';
import geoConfig from 'Config/geos';
import download from 'Services/download';
import buildFooter from 'Services/download/buildFooter';
import processCellCallback from 'Services/download/processCellCallback';
import cleanCSV from 'Services/download/cleanCSV';
import printTable from 'Services/printTable';
import utils from 'Utils';
import getRows from 'Selectors/table/getRows';
import csvConfig from 'Config/csv';
import tableScrollShadow from 'Utils/ui/tableScrollShadow';
import { actionTableColumnOrderChanged } from 'Actions/table';
import * as styles from './table.css';
import './theme-cds.css';

const { flatten } = utils.array;

class Table extends Component {
  constructor(props) {
    super(props);

    this.columnSize = this.columnSize.bind(this);

    // AG-Grid Functions
    this.onGridReady = this.onGridReady.bind(this);
    this.onModelUpdated = this.onModelUpdated.bind(this);
    this.onDragStopped = this.onDragStopped.bind(this);
    this.onColumnPinned = this.onColumnPinned.bind(this);

    // React Component functions
    this.onDownload = this.onDownload.bind(this);

    this.state = {
      columnState: props.columnState,
    };
  }

  componentDidUpdate() {
    if (this.state.columnState) {
      this.columnApi.setColumnState(this.state.columnState);
    }
  }

  onDragStopped() {
    this.columnApi.moveColumn('characteristic', 0);
    const columnState = this.columnApi.getColumnState();
    this.props.columnOrderChanged(columnState);
    this.setState({ columnState });
  }

  onColumnPinned({ column, pinned }) {
    if (pinned && 'characteristic' !== column.colId) {
      this.columnApi.setColumnPinned(column, null);
    }
  }

  onModelUpdated() {
    this.columnSize();
  }

  onGridReady({ api, columnApi }) {
    this.api = api;
    this.columnApi = columnApi;

    // Autosize characteristic column
    this.columnSize();

    if (this.props.print) {
      printTable.call(this);
    }

    if (this.state.columnState) {
      this.columnApi.setColumnState(this.state.columnState);
    }
  }

  onDownload() {
    const indicatorsById = this.props.indicators.reduce((acc, item) => (
      { ...acc, [item.id]: item }
    ), {});
    const activeIndicators = this.props.rowData.reduce((acc, item) => (
      'string' === typeof item.id && !item.children ?
      [...acc, indicatorsById[item.id]] :
      acc
    ), []);
    const csv = this.api.getDataAsCsv({
      processCellCallback,
      suppressQuotes: true, // We do this ourselves
      customHeader: csvConfig.header,
      customFooter: buildFooter(activeIndicators),
      shouldRowBeSkipped: ({ node: { data: { full, children = [] } } }) =>
        full && 0 !== children.length,
      processHeaderCallback:
        ({ column: { colId, colDef: { headerName } } }) => (
          'characteristic' !== colId ?
            `"${headerName}"` :
            '""'
        ),
    });

    download('raw_data.csv', cleanCSV(csv));
  }

  columnSize() {
    if (!this.columnApi) {
      return;
    }

    this.columnApi.autoSizeColumns(
      this.columnApi
        .getAllDisplayedColumns()
        .map(({ colId }) => colId)
    );
  }

  render() {
    const style = {
      width: '100%',
      height: '100%',
    };

    const {
      columnDefs,
      rowData,
      print,
    } = this.props;

    const options = {
      ...gridOptions,
      fullWidthCellRendererFramework: TableCellFullWidth,
    };

    if (print) {
      options.domLayout = 'forPrint';
    }

    const className = [
      'theme-cds',
      styles.wrapper,
      print ? 'print' : '',
    ].join(' ');

    const heightAcc = rowData.reduce((acc, row) => (
      (
        row.children ||
        ('undefined' !== typeof row.hasMega && false === row.hasMega)
      ) ? acc + 52 :
      acc + 30
    ), 0);

    const gridHeight = 500 < heightAcc ? 600 : heightAcc + 100;

    const wrapperStyle = {
      height: gridHeight,
    };

    return (
      <div>
        {!print && <TableToolbar onDownload={this.onDownload} /> }
        <div
          className={className}
          ref={(ref) => { this.tableWrapper = ref; }}
          style={wrapperStyle}
        >
          <AgGridReact
            gridOptions={options}
            onModelUpdated={this.onModelUpdated}
            onColumnPinned={this.onColumnPinned}
            onDragStopped={this.onDragStopped}
            onGridReady={this.onGridReady}
            onColumnResized={() => tableScrollShadow(gridHeight)}
            style={style}
            columnDefs={columnDefs}
            rowData={rowData}
            rowHeight="20"
          />
        </div>
        {!print && (
          <div className={styles.helpWrapper}>
            <div className={styles.helpTextGroup}>
              <span className={styles.helpText}>{helpText}</span>
              <span
                className={styles.helpText}
                dangerouslySetInnerHTML={{ __html: moreInfoHTML }} // eslint-disable-line react/no-danger
              />
            </div>
            <TableToolbar bottom onDownload={this.onDownload} />
          </div>
        )}
      </div>
    );
  }
}

Table.defaultProps = {
  columnState: null,
};

// @TODO build out this type def
Table.propTypes = {
  print: PropTypes.bool.isRequired,
  columnDefs: PropTypes.array.isRequired,
  rowData: PropTypes.array.isRequired,
  indicators: PropTypes.array.isRequired,
  columnOrderChanged: PropTypes.func.isRequired,
  columnState: PropTypes.array,
};

const mapStateToProps = (state) => ({
  print: state.data.print,
  rowData: getRows(state),
  indicators: state.table.indicators.map((item) => ({
    ...item,
    title: state.data.indicators[item.id].name,
  })) || [],
  columnDefs: flatten(geoConfig, 2)
    .filter(([val]) =>
      'all' === state.router.geos ||
      (state.router.geos === val || -1 < state.router.geos.indexOf(val))
    )
    .map(([field, headerName]) => ({
      field,
      headerName,
    })),
  columnState: state.table.columnState,
});

const mergeProps = (
  { print, columnDefs, rowData, indicators, columnState },
  { columnOrderChanged }
) => ({
  print,
  indicators,
  columnState,
  columnOrderChanged,
  columnDefs: [
    {
      headerName: '',
      field: 'characteristic',
      sort: 'asc',
      comparator: (
        x,
        y,
        { data: { id: a = '', sortId: aSortId = false } },
        { data: { id: b = '', sortId: bSortId = false } }
      ) => {
        if (aSortId && bSortId) {
          // Start with categories first
          if (aSortId.category && bSortId.category) {
            const {
              parent: aParent,
              key: aKey,
              itemOrdinal: aOrdinal,
            } = aSortId.category;
            const {
              parent: bParent,
              key: bKey,
              itemOrdinal: bOrdinal,
            } = bSortId.category;
            // Sort by parent category
            if ((aParent || 0 === aParent) &&
                (bParent || 0 === bParent) &&
                aParent !== bParent) {
              return aParent - bParent;
            }
            // Sort by subcategory
            if (aKey && bKey && aKey !== bKey) {
              // These keys are numeric, so we need to compare them as such
              return aKey.localeCompare(bKey, 'en', { numeric: true });
            }
            // Sort by order of indicators within subcategory/category
            if ((aOrdinal || 0 === aOrdinal) &&
                (bOrdinal || 0 === bOrdinal) &&
                aOrdinal !== bOrdinal) {
              return aOrdinal - bOrdinal;
            }
          }

          // Move onto row-specific sorting.
          const { itemOrdinal: aOrdinal } = aSortId;
          const { itemOrdinal: bOrdinal } = bSortId;

          // Sort by order of child indicators within mega indicator
          if ((aOrdinal || 0 === aOrdinal) &&
                (bOrdinal || 0 === bOrdinal) &&
                aOrdinal !== bOrdinal) {
            return aOrdinal - bOrdinal;
          }
          if (aSortId.id !== bSortId.id) {
            return aSortId.id.localeCompare(bSortId.id);
          }
          // Sort by order of characteristics within indicator
          if (aSortId.row && !bSortId.row) {
            return 1;
          } else if (!aSortId.row && bSortId.row) {
            return -1;
          }
          if (aSortId.row && bSortId.row) {
            return parseInt(aSortId.row.split('-')[0], 10) -
              parseInt(bSortId.row.split('-')[0], 10);
          }
        } else if (aSortId && !bSortId) {
          return aSortId.id.localeCompare(b);
        } else if (!aSortId && bSortId) {
          return a.localeCompare(bSortId.id);
        }
        return a.localeCompare(b);
      },
    },
    ...columnDefs,
  ].map(({ headerName, field, ...rest }) => ({
    headerName,
    field,
    ...rest,
    cellRendererFramework: 'characteristic' === field ?
      TableCellCharacteristic :
      TableCell,
    suppressMovable: 'characteristic' === field,
    pinned: 'characteristic' === field ? 'left' : null,
    headerComponentFramework: TableHeader,
  })),
  rowData,
});

const mapDispatchToProps = (dispatch) => ({
  columnOrderChanged: (columnState) =>
    dispatch(actionTableColumnOrderChanged(columnState)),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(Table);

export { Table, mapStateToProps, mergeProps };



// WEBPACK FOOTER //
// ./app/components/table/index.js